/* eslint-disable react/display-name */
import React from 'react';
import axios from 'helpers/axios';

import { AdminLayout } from 'ui/components/layouts';
import { Title, Container } from 'ui/components/styled';
import { DataTable, DatePicker, Drawer } from 'ui/components/shared';
import {
  useToasts,
  useOpenState,
  useMutableRef,
  useBlobDownload,
  useCancelToken,
  useLocalStorage,
} from 'hooks';
import { DownloadButton } from './styled';
import { ReportsByCustomer } from './lists';
import { lastDayOfPreviousMonth, formatDate, isBeforeToday } from 'helpers/date';

export const Reports = () => {
  const [data, setData] = React.useState([]);
  const [footer, setFooter] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [perPage, setPerPage] = React.useState(15);
  const [sort, setSort] = React.useState({ column: '', order: '' });
  const [search, setSearch] = React.useState('');
  const [date, setDate] = useLocalStorage(
    'reportsDate',
    lastDayOfPreviousMonth(),
    (value) => new Date(value)
  );
  const { errorToast } = useToasts();
  const [drawer, openDrawer, closeDrawer] = useOpenState();
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
  const columns = [
    {
      name: 'Company name',
      selector: 'company.name',
      sortable: true,
      minWidth: '350px',
    },
    {
      name: 'Total Monitors',
      selector: 'jobs.total',
      sortable: true,
      minWidth: '175px',
      //center: true,
      style: { paddingLeft: '20px' },
    },
    {
      name: 'Success',
      selector: 'jobs.success',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Failed',
      selector: 'jobs.failed',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Warning',
      selector: 'jobs.warning',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Late',
      selector: 'jobs.late',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Uninitialized',
      selector: 'jobs.unknown',
      minWidth: '120px',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: '',
      grow: 1,
    },
  ];

  const fetchTotal = async (data, search, date) => {
    try {
      const response = await axios.get(`/reports/byCompany/total/?$search=${search}&$date=${date}`);

      if (response.status === 200) {
        setFooter([
          {
            company: { name: 'Page Total' },
            jobs: data.reduce(
              (acc, { jobs }) => ({
                total: acc.total + jobs.total,
                unknown: acc.unknown + jobs.unknown,
                success: acc.success + jobs.success,
                failed: acc.failed + jobs.failed,
                warning: acc.warning + jobs.warning,
                late: acc.late + jobs.late,
              }),
              {
                total: 0,
                unknown: 0,
                success: 0,
                failed: 0,
                warning: 0,
                late: 0,
              }
            ),
          },
          {
            company: { name: 'Grand Total' },
            jobs: response.data,
          },
        ]);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Total.' });
    }
  };

  const fetchBillingReports = async (page) => {
    setLoading(true);

    try {
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCompany/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Billing Reports.' });
    }

    setLoading(false);
  };

  const handleRowsPerPageChange = async (newPerPage, page) => {
    setLoading(true);
    setCurrentPage(page);

    try {
      const skip = page > 1 ? (page - 1) * newPerPage : 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCompany/?$top=${newPerPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setPerPage(newPerPage);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Rows Change.' });
    }

    setLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBillingReports(page);
  };

  const handleSort = async (column, sortDirection) => {
    setLoading(true);

    const sortColumn = column?.selector || '';
    const sortOrder = sortColumn ? sortDirection : '';
    setSort({ column: sortColumn, order: sortOrder });

    try {
      const skip = 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCompany/?$top=${perPage}&$skip=${skip}&$orderby=${sortColumn}&$order=${sortOrder}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Sort Billing Reports.' });
    }

    setLoading(false);
  };

  const handleFilter = async (value) => {
    setLoading(true);
    setSearch(value);

    cancelPreviousRequest();

    try {
      const skip = 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCompany/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${value}&$date=${formattedDate}`,
        { cancelToken: newCancelToken() }
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, value, formattedDate);
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: 'Error: Filter Billing Reports.' });
    }

    setLoading(false);
  };

  const handleDateFilter = async (value) => {
    setLoading(true);
    setDate(value);

    const formattedDate = formatDate(value);
    try {
      const page = currentPage.current;
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const response = await axios.get(
        `/reports/byCompany/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Filter Billing Reports.' });
    }

    setLoading(false);
  };

  const handleRowClick = ({ company }) => {
    openDrawer(true, { company });
  };

  React.useEffect(() => {
    fetchBillingReports(1);
  }, []);

  const renderSubHeaderActions = () => {
    const downloadLink = React.useMemo(() => {
      const formattedDate = formatDate(date);
      return `/reports/export/byCompany/?$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`;
    }, [sort, search, date]);
    const download = useBlobDownload(downloadLink);

    return (
      <Container flexDirection="row" alignItems="center" width="auto">
        <DownloadButton
          mr="m"
          onClick={download.onClick}
          disabled={!data?.length || download.disabled}
        >
          Download
        </DownloadButton>
        <DatePicker
          placeholder="Date"
          format="MM/dd/yyyy"
          value={date}
          onChange={handleDateFilter}
        />
      </Container>
    );
  };

  return (
    <AdminLayout>
      <Title variant="dark" mx="m" mb="l">
        Billing reports
      </Title>
      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        paginationPerPage={perPage}
        paginationTotalRows={totalRows}
        paginationCurrentPage={currentPage.current}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
        onPageReset={setCurrentPage}
        onSort={handleSort}
        filterPlaceholder="Company Search"
        onFilter={handleFilter}
        subHeaderActions={renderSubHeaderActions()}
        footer={footer}
        onRowClicked={handleRowClick}
        // noDataMessage={
        //   <NoReports
        //     date={date}
        //     onLoadingChange={setLoading}
        //     onSuccess={() => fetchBillingReports(1)}
        //     onError={errorToast}
        //   />
        // }
        rowHeight="41px"
        pointerOnHover
      />
      <Drawer show={drawer.isOpen()} onClose={closeDrawer} width="60%">
        {drawer.params?.company && (
          <ReportsByCustomer company={drawer.params?.company} date={date} />
        )}
      </Drawer>
    </AdminLayout>
  );
};

// eslint-disable-next-line no-unused-vars
const NoReports = ({ date, onLoadingChange, onSuccess, onError }) => {
  const setLoading = (value) => {
    if (onLoadingChange) {
      onLoadingChange(value);
    }
  };

  const generateReports = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`/reports/generate/?$date=${formatDate(date)}`);

      if (response.status === 200 && onSuccess) {
        onSuccess();
        return;
      }
    } catch (error) {
      if (onError) {
        onError({ error, default: 'Error: Generate Billing Reports.' });
      }
    }

    setLoading(false);
  };

  return (
    <>
      There are no records to display.&nbsp;
      {isBeforeToday(date) && <a onClick={generateReports}>Click here to generate the reports.</a>}
    </>
  );
};
